/**
 * Chrome extension / Outlook panel: sends the firm's branding (logo, name, sidebar colours, as
 * configured in tenant settings) to the panel, so its top bar matches the app inside it. Same
 * logo choice as the sidebar: the light logo on a dark sidebar, the dark one on a light sidebar.
 */
import { useEffect } from 'react';
import { useBrandingLogos } from '@/api';
import { useTheme } from '@/theme';
import { useTenant } from '@/tenant';
import { useAuth } from '@/auth';
import { reportPanelBranding } from '@/utils/extensionHost';

const absolute = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try { return new URL(url, window.location.origin).href; } catch { return null; }
};

export const PanelBrandingReporter = () => {
  const { theme } = useTheme();
  const { settings, companyName } = useTenant();
  // In a panel the firm is not in the address, so its name may only be known from the sign-in.
  const { currentTenantName, featuresLoaded } = useAuth();
  const name = companyName || currentTenantName || 'mot-r';
  const { data: brandingLogos, isPending: isBrandingPending } = useBrandingLogos();

  const dark = theme.sidebar.sidebarMode === 'dark';
  const lightLogo = theme.sidebar.logoLightUrl || brandingLogos?.lightLogoUrl || settings.logoLightUrl;
  const darkLogo = theme.sidebar.logoDarkUrl || brandingLogos?.darkLogoUrl || settings.logoDarkUrl;
  const themed = dark ? (lightLogo || darkLogo) : (darkLogo || lightLogo);
  // Without uploaded logos the app falls back to "<blob>/www/<tenant name>_Logo.png", the tenant
  // name coming from the subdomain. A panel has no subdomain (it may resolve the tenant id
  // instead), so use the signed-in firm's name, as the firm's own address would.
  const fallbackLogo = settings.logoUrl && currentTenantName
    ? settings.logoUrl.replace(/\/[^/]*_Logo\.png$/i, `/${encodeURIComponent(currentTenantName)}_Logo.png`)
    : settings.logoUrl;
  const logoUrl = absolute(themed || fallbackLogo);
  const logoFilter = !themed && fallbackLogo && dark ? 'brightness(0) invert(1)' : null;
  // Once the logos answered and the signed-in firm is known (application-configuration), so the
  // bar does not flash mot-r's own look first. Not gated on the tenant lookup: in a panel the firm
  // comes from the sign-in, not a subdomain, so that lookup may never settle.
  const ready = !isBrandingPending && featuresLoaded;

  useEffect(() => {
    if (!ready) return;
    reportPanelBranding({
      name,
      logoUrl,
      logoFilter,
      background: theme.sidebar.background,
      text: theme.sidebar.text,
    });
  }, [ready, name, logoUrl, logoFilter, theme.sidebar.background, theme.sidebar.text]);

  return null;
};
